import { useEffect, useState } from "react";
import { unregisterServiceWorkers } from "@/lib/cacheCleanup";

// Vite injects this at build time. We expose it here so the debug chip can
// show the exact build identity that the live HTML is currently serving.
declare const __BUILD_ID__: string;
declare const __BUILD_TIME__: string;

const STOCK_KEY_PATTERNS = [/^finplay_/i, /^finbite_/i, /^dailyChallenge/i];

interface Snapshot {
  buildId: string;
  buildTime: string;
  swCount: number;
  swController: boolean;
  cacheCount: number;
  cacheNames: string[];
  localStorageKeys: number;
  sessionStorageKeys: number;
  finplayKeys: string[];
  stockCount: number | "n/a";
  href: string;
  ua: string;
}

const isDebug = () => {
  if (typeof window === "undefined") return false;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("debug") === "1") return true;
    if (sessionStorage.getItem("finplay:debug") === "1") return true;
  } catch {}
  return false;
};

const collect = async (): Promise<Snapshot> => {
  let swCount = 0;
  let swController = false;
  if ("serviceWorker" in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      swCount = regs.length;
      swController = !!navigator.serviceWorker.controller;
    } catch {}
  }
  let cacheNames: string[] = [];
  if ("caches" in window) {
    try { cacheNames = await caches.keys(); } catch {}
  }
  const lsKeys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k) lsKeys.push(k);
    }
  } catch {}
  const finplayKeys = lsKeys.filter((k) => STOCK_KEY_PATTERNS.some((p) => p.test(k)));

  let stockCount: number | "n/a" = "n/a";
  try {
    const grid = document.querySelector('[data-testid="stock-grid-count"]');
    if (grid?.textContent) {
      const m = grid.textContent.match(/(\d+)/);
      if (m) stockCount = parseInt(m[1], 10);
    }
  } catch {}

  return {
    buildId: typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "dev",
    buildTime: typeof __BUILD_TIME__ !== "undefined" ? __BUILD_TIME__ : "dev",
    swCount,
    swController,
    cacheCount: cacheNames.length,
    cacheNames,
    localStorageKeys: lsKeys.length,
    sessionStorageKeys: (() => { try { return sessionStorage.length; } catch { return 0; } })(),
    finplayKeys,
    stockCount,
    href: window.location.href,
    ua: navigator.userAgent,
  };
};

const clearAppCache = async () => {
  try {
    await unregisterServiceWorkers({ reason: "debug-clear-cache", reload: false });
  } finally {
    const url = new URL(window.location.href);
    url.searchParams.set("cache-reset", Date.now().toString());
    url.searchParams.set("debug", "1");
    window.location.replace(url.toString());
  }
};

const DebugPanel = () => {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [snap, setSnap] = useState<Snapshot | null>(null);

  useEffect(() => {
    if (!isDebug()) return;
    setEnabled(true);
    try { sessionStorage.setItem("finplay:debug", "1"); } catch {}
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const refresh = async () => {
      const s = await collect();
      if (!cancelled) setSnap(s);
    };
    refresh();
    const id = window.setInterval(refresh, 3000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 12,
        right: 12,
        zIndex: 99999,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 11,
        lineHeight: 1.35,
        color: "#0f172a",
      }}
    >
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          style={{
            background: "#0f172a", color: "#fff", border: "1px solid #fff",
            padding: "6px 10px", borderRadius: 999, cursor: "pointer",
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
          }}
        >
          🛠 debug · {snap?.buildId?.slice(0, 7) ?? "…"}
        </button>
      ) : (
        <div
          style={{
            background: "rgba(255,255,255,0.98)",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 12,
            width: 320,
            maxHeight: "70vh",
            overflow: "auto",
            boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <strong>FinPlay Debug</strong>
            <button onClick={() => setOpen(false)} style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 14 }}>×</button>
          </div>
          {snap ? (
            <div style={{ display: "grid", gap: 4 }}>
              <Row k="Build ID" v={snap.buildId} />
              <Row k="Built at" v={snap.buildTime} />
              <Row k="SW registrations" v={`${snap.swCount}${snap.swController ? " (controlling)" : ""}`} />
              <Row k="CacheStorage entries" v={`${snap.cacheCount}`} />
              {snap.cacheNames.length > 0 && (
                <div style={{ color: "#64748b", fontSize: 10 }}>{snap.cacheNames.join(", ")}</div>
              )}
              <Row k="localStorage keys" v={`${snap.localStorageKeys}`} />
              <Row k="sessionStorage keys" v={`${snap.sessionStorageKeys}`} />
              <Row k="finplay_* keys" v={`${snap.finplayKeys.length}`} />
              <Row k="Stock grid count" v={`${snap.stockCount}`} />
              <div style={{ color: "#64748b", fontSize: 10, wordBreak: "break-all" }}>{snap.href}</div>
            </div>
          ) : (
            <div>Collecting…</div>
          )}
          <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
            <button
              onClick={() => { collect().then(setSnap); }}
              style={btnStyle("#e2e8f0", "#0f172a")}
            >
              Refresh
            </button>
            <button
              onClick={clearAppCache}
              style={btnStyle("#0f766e", "#fff")}
            >
              Clear App Cache
            </button>
            <button
              onClick={() => {
                try { sessionStorage.removeItem("finplay:debug"); } catch {}
                const url = new URL(window.location.href);
                url.searchParams.delete("debug");
                url.searchParams.delete("cache-reset");
                window.location.replace(url.toString());
              }}
              style={btnStyle("#f1f5f9", "#0f172a")}
            >
              Disable
            </button>
          </div>
          <div style={{ marginTop: 8, color: "#64748b", fontSize: 10 }}>
            Clear App Cache removes service workers and browser CacheStorage only. It does not clear portfolio, login, or local/session data.
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ k, v }: { k: string; v: string }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
    <span style={{ color: "#64748b" }}>{k}</span>
    <span style={{ fontWeight: 600, textAlign: "right", wordBreak: "break-all" }}>{v}</span>
  </div>
);

const btnStyle = (bg: string, fg: string): React.CSSProperties => ({
  background: bg, color: fg, border: "none", borderRadius: 8,
  padding: "6px 10px", cursor: "pointer", fontSize: 11, fontWeight: 600,
});

export default DebugPanel;
